import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Song, GameResult } from '../types';
import { useLanguage } from '../LanguageContext';
import './Game.css';

interface GameProps {
  song: Song;
  onGameEnd: (result: GameResult) => void;
  onBack: () => void;
  authToken?: string;
}

interface BackgroundParticle {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  rotation: number;
  rotationSpeed: number;
}

const MAX_NORMALIZED_SCORE = 100000;

const Game: React.FC<GameProps> = ({ song, onGameEnd, onBack, authToken }) => {
  const { t } = useLanguage();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [perfectHits, setPerfectHits] = useState(0);
  const [goodHits, setGoodHits] = useState(0);
  const [missedHits, setMissedHits] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [hitEffect, setHitEffect] = useState<{ x: number; y: number; type: string } | null>(null);
  const [particles, setParticles] = useState<BackgroundParticle[]>([]);

  const beatMap = song.beatMap;
  const totalBeats = beatMap.beats.length;
  const brandingTheme = song.brandingTheme || {};
  const primaryColor = brandingTheme.primaryColor || '#eb6623';
  const secondaryColor = brandingTheme.secondaryColor || '#ff8042';

  useEffect(() => {
    const newParticles: BackgroundParticle[] = [];
    for (let i = 0; i < 15; i++) {
      newParticles.push({
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 60 + 20,
        speed: Math.random() * 0.3 + 0.1,
        opacity: Math.random() * 0.15 + 0.05,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 0.5,
      });
    }
    setParticles(newParticles);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setParticles(prev => prev.map(p => ({
        ...p,
        y: (p.y + p.speed) % 120,
        rotation: p.rotation + p.rotationSpeed,
      })));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    if (!container) return;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
  }, []);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      finishGame();
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [score, maxCombo, perfectHits, goodHits, missedHits]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const laneWidth = width / song.lanes;
    const hitZoneY = height - 100;
    const beatTravelTime = 2000;
    const startY = -40;

    let animationId: number;
    let startTime: number;
    const hitBeats = new Set<number>();

    const draw = (currentTime: number) => {
      ctx.clearRect(0, 0, width, height);

      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, 'rgba(10, 10, 15, 0.95)');
      gradient.addColorStop(0.5, 'rgba(15, 12, 20, 0.95)');
      gradient.addColorStop(1, 'rgba(10, 10, 15, 0.98)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      for (let i = 0; i <= song.lanes; i++) {
        const x = i * laneWidth;
        const laneGradient = ctx.createLinearGradient(x, 0, x, height);
        laneGradient.addColorStop(0, 'rgba(255, 255, 255, 0.02)');
        laneGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.08)');
        laneGradient.addColorStop(1, 'rgba(255, 255, 255, 0.03)');
        ctx.strokeStyle = laneGradient;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      const hitZoneGradient = ctx.createLinearGradient(0, hitZoneY - 30, 0, hitZoneY + 30);
      hitZoneGradient.addColorStop(0, 'transparent');
      hitZoneGradient.addColorStop(0.4, `${primaryColor}20`);
      hitZoneGradient.addColorStop(0.5, `${primaryColor}40`);
      hitZoneGradient.addColorStop(0.6, `${primaryColor}20`);
      hitZoneGradient.addColorStop(1, 'transparent');
      ctx.fillStyle = hitZoneGradient;
      ctx.fillRect(0, hitZoneY - 30, width, 60);

      ctx.strokeStyle = primaryColor;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(0, hitZoneY);
      ctx.lineTo(width, hitZoneY);
      ctx.stroke();

      ctx.shadowColor = primaryColor;
      ctx.shadowBlur = 15;
      ctx.stroke();
      ctx.shadowBlur = 0;

      for (let i = 0; i < song.lanes; i++) {
        const centerX = i * laneWidth + laneWidth / 2;
        ctx.beginPath();
        ctx.arc(centerX, hitZoneY, 35, 0, 2 * Math.PI);
        ctx.strokeStyle = `${primaryColor}50`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      beatMap.beats.forEach((beat, index) => {
        if (hitBeats.has(index)) return;
        
        const elapsed = currentTime - startTime;
        const beatStartTime = beat.t_ms - beatTravelTime;
        const progress = (elapsed - beatStartTime) / beatTravelTime;
        
        if (progress < 0 || progress > 1.2) return;
        
        const y = startY + (hitZoneY - startY) * progress;
        const x = (beat.lane - 1) * laneWidth + laneWidth / 2;
        const baseRadius = 26;
        const pulseRadius = baseRadius + Math.sin(elapsed / 100) * 3;

        ctx.beginPath();
        const beatGradient = ctx.createRadialGradient(x, y, 0, x, y, pulseRadius);
        beatGradient.addColorStop(0, secondaryColor);
        beatGradient.addColorStop(0.6, primaryColor);
        beatGradient.addColorStop(1, `${primaryColor}80`);
        ctx.fillStyle = beatGradient;
        ctx.arc(x, y, pulseRadius, 0, 2 * Math.PI);
        ctx.fill();

        ctx.shadowColor = primaryColor;
        ctx.shadowBlur = 20;
        ctx.strokeStyle = `${secondaryColor}80`;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.shadowBlur = 0;

        ctx.beginPath();
        ctx.arc(x, y - 5, pulseRadius * 0.4, 0, Math.PI, true);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.fill();
      });

      animationId = requestAnimationFrame(draw);
    };

    if (gameStarted) {
      startTime = performance.now();
      draw(startTime);
    } else {
      ctx.clearRect(0, 0, width, height);
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, 'rgba(10, 10, 15, 0.95)');
      gradient.addColorStop(1, 'rgba(10, 10, 15, 0.98)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      for (let i = 0; i <= song.lanes; i++) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(i * laneWidth, 0);
        ctx.lineTo(i * laneWidth, height);
        ctx.stroke();
      }
    }

    return () => cancelAnimationFrame(animationId);
  }, [gameStarted, song, beatMap.beats, primaryColor, secondaryColor]);

  const startGame = () => {
    setCountdown(3);
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(countdownInterval);
          setGameStarted(true);
          setCountdown(null);
          if (audioRef.current) {
            audioRef.current.play().catch(console.error);
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!gameStarted) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    let clientX: number;
    let clientY: number;
    
    if ('touches' in e) {
      clientX = e.touches[0]?.clientX || e.changedTouches[0]?.clientX;
      clientY = e.touches[0]?.clientY || e.changedTouches[0]?.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const hitZoneY = canvas.height - 100;
    const distance = Math.abs(y - hitZoneY);

    let hitType = 'miss';
    let points = 0;

    if (distance < 30) {
      hitType = 'perfect';
      points = 100;
      setPerfectHits(p => p + 1);
    } else if (distance < 60) {
      hitType = 'good';
      points = 50;
      setGoodHits(g => g + 1);
    } else {
      setMissedHits(m => m + 1);
      setCombo(0);
    }

    if (points > 0) {
      setHitEffect({ x, y: hitZoneY, type: hitType });
      setTimeout(() => setHitEffect(null), 300);

      setScore(prev => prev + points * (1 + Math.floor(combo / 10) * 0.1));
      setCombo(prev => {
        const newCombo = prev + 1;
        if (newCombo > maxCombo) setMaxCombo(newCombo);
        return newCombo;
      });
    }
  };

  const finishGame = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const totalHits = perfectHits + goodHits + missedHits;
    const accuracy = totalHits > 0 
      ? Math.round((perfectHits + goodHits * 0.5) / totalHits * 100)
      : 0;
    
    const rawScore = Math.round(score);
    const normalizedScore = totalBeats > 0 
      ? Math.round((rawScore / (totalBeats * 100)) * MAX_NORMALIZED_SCORE)
      : rawScore;

    let isNewBest = false;
    let previousBest: number | undefined;

    if (authToken) {
      try {
        const res = await fetch('/api/scores', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            songId: parseInt(song.id),
            score: rawScore,
            accuracy,
            comboMax: maxCombo,
            perfectHits,
            goodHits,
            missedHits,
            totalBeats,
          })
        });
        const data = await res.json();
        isNewBest = data.isNewBest;
        previousBest = data.previousBest;
      } catch (err) {
        console.error('Failed to save score:', err);
      }
    }

    const result: GameResult = {
      score: rawScore,
      normalizedScore,
      accuracy,
      maxCombo,
      perfectHits,
      goodHits,
      missedHits,
      totalBeats,
      isNewBest,
      previousBest,
      songTitle: song.title,
    };

    onGameEnd(result);
  };

  return (
    <div className="game" style={{ '--primary-color': primaryColor, '--secondary-color': secondaryColor } as React.CSSProperties}>
      <div className="game-background-effects">
        {particles.map((p, i) => (
          <div
            key={i}
            className="floating-shape"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
              opacity: p.opacity,
              transform: `rotate(${p.rotation}deg)`,
              background: `linear-gradient(135deg, ${primaryColor}30, ${secondaryColor}20)`,
            }}
          />
        ))}
      </div>

      <header className="game-header">
        <button className="game-back" onClick={onBack}>←</button>
        <div className="game-title-wrap">
          <h2 className="game-title">{song.title}</h2>
          <p className="game-artist">{song.artistName || song.creator}</p>
        </div>
        <div className="game-bpm">{beatMap.bpm} BPM</div>
      </header>

      <div className="game-stats">
        <div className="stat">
          <span className="stat-value">{Math.round(score).toLocaleString()}</span>
          <span className="stat-label">{t.game.score}</span>
        </div>
        <div className="stat stat-combo">
          <span className="stat-value">{combo}x</span>
          <span className="stat-label">{t.game.combo}</span>
        </div>
      </div>

      <div className="game-canvas-container">
        <canvas 
          ref={canvasRef} 
          className="game-canvas"
          onClick={handleCanvasClick}
          onTouchStart={handleCanvasClick}
        />
        
        {hitEffect && (
          <div 
            className={`hit-effect hit-${hitEffect.type}`}
            style={{ left: hitEffect.x, top: hitEffect.y }}
          >
            {hitEffect.type === 'perfect' ? t.game.perfect + '!' : t.game.good + '!'}
          </div>
        )}
        
        {countdown !== null && (
          <div className="countdown">{countdown}</div>
        )}
        
        {!gameStarted && countdown === null && (
          <div className="game-overlay">
            <button className="btn btn-primary btn-large" onClick={startGame}>
              ▶ Start
            </button>
          </div>
        )}
      </div>

      <audio ref={audioRef} src={song.audioUrl} preload="auto" />
    </div>
  );
};

export default Game;
